import { useState } from 'react'
import { Keyboard, Microphone } from 'iconsax-react'
import type { Status } from './chatMachine'
import { HoldToTalkButton, type Gesture } from './HoldToTalkButton'
import { InterruptButton } from './InterruptButton'
import { StatusLine } from './StatusLine'

export function VoiceControls({
    muted,
    status,
    statusText,
    isReplying,
    capturing,
    onInterrupt,
    onToggleMute,
    onEnterText,
    onHoldStart,
    onHoldStop,
    onHoldCancel,
}: {
    muted: boolean
    status: Status
    statusText: string
    isReplying: boolean
    capturing: boolean
    onInterrupt: () => void
    onToggleMute: () => void
    onEnterText: () => void
    onHoldStart: () => void
    onHoldStop: () => void
    onHoldCancel: () => void
}) {
    // 长按手势态(上报自胶囊):音波统一画在上方状态行,长按时隐藏文字、取消态转红
    const [gesture, setGesture] = useState<Gesture>('idle')
    const holding = gesture !== 'idle'
    return (
        <div className="hairline-t relative px-4 pt-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))]">
            <StatusLine
                text={statusText}
                active={Boolean(statusText) && !holding}
                listening={status === 'listening' && !muted && !holding}
                capturing={capturing || holding}
                danger={gesture === 'cancel'}
            />
            <div className="flex items-center justify-center gap-4">
                {/* AI 思考/说话时,左键由"静音"换成"打断"(静音只在 listening 有意义,半双工下 AI 说话时麦克风已暂停)。 */}
                {isReplying ? (
                    <InterruptButton onClick={onInterrupt} />
                ) : (
                    <button
                        onClick={onToggleMute}
                        aria-label={muted ? '取消静音' : '静音'}
                        className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-full transition active:scale-95 ${
                            muted
                                ? 'bg-destructive text-white shadow-lg shadow-destructive/30'
                                : 'glass bg-bg-secondary text-text-primary'
                        }`}
                    >
                        {/* 静音 = 聆听同一个麦克风,再叠一道斜杠(iconsax 自带的 MicrophoneSlash 是另一套造型,不统一)。
                            斜杠用 ring-destructive 做描边,在红底上"挖"出缝隙,避免和白色麦克风笔画糊在一起。 */}
                        <span className="relative inline-flex items-center justify-center">
                            <Microphone size={27} color="currentColor" variant="Linear" />
                            {muted && (
                                <span
                                    aria-hidden
                                    className="pointer-events-none absolute left-1/2 top-1/2 h-[2px] w-[26px] -translate-x-1/2 -translate-y-1/2 -rotate-45 rounded-full bg-current ring-[1.5px] ring-destructive"
                                />
                            )}
                        </span>
                    </button>
                )}

                {/* 按住说话(手动):仅聆听中可按。把会话收尾从 VAD 静音切到松手,卡壳/多句也能完整收集 */}
                <HoldToTalkButton
                    enabled={status === 'listening'}
                    onStart={onHoldStart}
                    onStop={onHoldStop}
                    onCancel={onHoldCancel}
                    onGesture={setGesture}
                />

                {/* 切回文字(退出语音的出口之一;另一个出口是顶部"返回") */}
                <button
                    onClick={onEnterText}
                    disabled={isReplying}
                    aria-label="文字输入"
                    className="glass flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-bg-secondary text-text-primary transition active:scale-95 disabled:opacity-40"
                >
                    <Keyboard size={30} color="currentColor" variant="Linear" />
                </button>
            </div>
        </div>
    )
}
